import React from "react";
import { ReactSVG } from "react-svg";
import { setSVGColor } from "../SVGHelper";
import "../style/colors.scss";
import "../style/typography.scss";
import useOptionallyControlled from "../hooks/useOptionallyControlled";

import squareCheckedSVG from "../icon/Checkbox/SquareChecked.svg";
import squareUncheckedSVG from "../icon/Checkbox/SquareUnchecked.svg";
import squareIntermediateSVG from "../icon/Checkbox/SquareIntermediate.svg";
import roundCheckedSVG from "../icon/Checkbox/RoundChecked.svg";
import roundUncheckedSVG from "../icon/Checkbox/RoundUnchecked.svg";
import roundIntermediateSVG from "../icon/Checkbox/RoundIntermediate.svg";

const ICON_SIZE_LARGE = "24px";
const ICON_SIZE_SMALL = "16px";

/**
 * Checkbox component with optional label and positions/sizes them according to the brand guide.
 * Supports controlled use with value prop OR uncontrolled use in form context, but don't mix them.
 */
const Checkbox = ({
    label,
    name,
    round,
    small,
    smallIcon, // like small but doesn't apply to label
    formOptions,
    disabled,
    value: controlledValue,
    onChange,
    intermediate,
    style = {},
    className = "",
    children,
}) => {
    const [propsForInput, setValue, useValue] = useOptionallyControlled(name, controlledValue, onChange, formOptions);
    const value = useValue();

    const iconSize = small || smallIcon ? ICON_SIZE_SMALL : ICON_SIZE_LARGE;
    const disabledColor = "var(--colors-primary-slate-300)";
    const color = "var(--colors-main-2)";

    const handleClick = (e) => {
        e.stopPropagation();
        if (disabled) {
            return;
        }

        setValue(!value);
    };

    const CheckmarkImg = ({ svg, isVisible }) => {
        return (
            <ReactSVG
                src={svg}
                style={isVisible ? {} : { display: "none" }}
                beforeInjection={(svg) => {
                    svg.setAttribute("width", iconSize);
                    svg.setAttribute("height", iconSize);
                    setSVGColor("currentColor", false, ["white"])(svg);
                }}
                onClick={handleClick}
            />
        );
    };

    return (
        <div
            className={`${small ? "paragraph-5-semibold" : "paragraph-4-semibold"} ${className}`}
            style={{
                // the weird ordering here allows style to override only active color(variable more work to support disabled color)
                ...(!disabled ? { color: color } : {}),
                display: "flex",
                gap: "4px",
                alignItems: "center",
                width: "fit-content",
                ...style,
                ...(disabled ? { color: disabledColor } : {}),
            }}
        >
            <input style={{ display: "none" }} type="checkbox" {...propsForInput} />

            {/* Loading all of them upfront to prevent delay. */}
            <CheckmarkImg svg={squareIntermediateSVG} isVisible={intermediate && !round} />
            <CheckmarkImg svg={roundIntermediateSVG} isVisible={intermediate && round} />
            <CheckmarkImg svg={squareCheckedSVG} isVisible={value && !round && !intermediate} />
            <CheckmarkImg svg={squareUncheckedSVG} isVisible={!value && !round && !intermediate} />
            <CheckmarkImg svg={roundCheckedSVG} isVisible={value && round && !intermediate} />
            <CheckmarkImg svg={roundUncheckedSVG} isVisible={!value && round && !intermediate} />

            {(children || label) && (
                <label onClick={handleClick}>
                    {label}
                    {children}
                </label>
            )}
        </div>
    );
};

export default Checkbox;
